import React, { useState, useCallback } from 'react';
import update from 'immutability-helper';

import Interaction from 'components/Practice/Interaction';
import { CustomDragLayer } from 'components/Practice/Interaction/CustomDragLayer';
import TextArea from 'components/shared/debouncedTextArea';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import QuestionOptions from 'components/editor/element/question/QuestionOptions';
import DNDScrollZone from 'components/shared/DNDScrollZone';
import { Icon } from 'ui';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';

export default function Conversation({
  title,
  onChange,
  content,
  handleUpdate,
  applyChanges,
  ...props
}) {
  const { conversation_data: conversationData = {}, id: questionId } = content;

  const [interactions, setInteractions] = useState(
    conversationData.interactions,
  );

  const [shouldCollapseInteractions, setShouldCollapseInteractions] =
    useState(false);
  const [customerName, setCustomerName] = useState(
    conversationData.customer_name,
  );
  const [conversationTitle, setConversationTitle] = useState(
    conversationData.conversation_title,
  );

  const onUpdateCustomerName = async value => {
    setCustomerName(value);

    try {
      await request.put(
        routes.QUESTIONS.METADATUM(
          questionId,
          conversationData.customer_name_metadatum_id,
        ),
        { value },
      );
      onChange({ conversation_data: { customer_name: value } });
    } catch (error) {
      console.error(error);
    }
  };

  const onUpdateConversationTitle = async value => {
    setConversationTitle(value);

    try {
      await request.put(
        routes.QUESTIONS.METADATUM(
          questionId,
          conversationData.conversation_title_metadatum_id,
        ),
        { value },
      );
      onChange({ conversation_data: { conversation_title: value } });
    } catch (error) {
      console.error(error);
    }
  };

  const onUpdateInteraction = interactionId => async body => {
    const newInteractions = interactions.map(int =>
      int.id === interactionId ? { ...body } : int,
    );
    setInteractions(newInteractions);
    try {
      await request.put(
        routes.QUESTIONS.INTERACTIONS(questionId, interactionId),
        body,
      );
      onChange({ conversation_data: { interactions: newInteractions } });
    } catch (error) {
      console.error(error);
    }
  };

  const onCreateInteraction = async () => {
    try {
      const data = await request.post(
        routes.QUESTIONS.INTERACTIONS_CREATE(questionId),
        {
          position: interactions.length + 1,
        },
      );
      onChange({
        conversation_data: {
          interactions: [...interactions, data.interaction],
        },
      });
      setInteractions([...interactions, data.interaction]);
    } catch (error) {
      console.error(error);
    }
  };

  const onDeleteInteraction = async interactionToDelete => {
    const updatedInteractions = interactions
      .filter(int => int.id !== interactionToDelete.id)
      .map((int, index) => {
        return { ...int, position: index + 1 };
      });

    try {
      await request.delete(
        routes.QUESTIONS.INTERACTIONS(questionId, interactionToDelete.id),
      );
      onChange(
        {
          conversation_data: {
            ...conversationData,
            interactions: updatedInteractions,
          },
        },
        { mergeChanges: false },
      );
      setInteractions(updatedInteractions);
    } catch (error) {
      console.error(error);
    }
  };

  const onUpdateResponse = interactionId => async (responseId, body) => {
    const newInteractions = interactions.map(int =>
      int.id === interactionId
        ? int.responses.map(resp => (resp.id === responseId ? body : resp))
        : int,
    );

    try {
      await request.put(
        routes.QUESTIONS.RESPONSES(questionId, interactionId, responseId),
        body,
      );
      onChange({ conversation_data: { interactions: newInteractions } });
    } catch (error) {
      console.error(error);
    }
  };

  const onAddResponse = interactionId => body => {
    return request
      .post(routes.QUESTIONS.ADD_RESPONSE(questionId, interactionId), body)
      .catch(console.error);
  };

  const onDeleteResponse = interactionId => responseId => {
    return request
      .delete(routes.QUESTIONS.RESPONSES(questionId, interactionId, responseId))
      .catch(console.error);
  };

  const reorderInteractions = newIndex => {
    const updatedInteraction = interactions[newIndex];
    request.put(
      routes.QUESTIONS.INTERACTIONS(questionId, updatedInteraction.id),
      updatedInteraction,
    );
  };

  const reorderInteractionsInterface = (newIndex, oldIndex) => {
    const updatedInteractions = interactions.map((int, index) => {
      if (index === oldIndex) {
        const updatedInteraction = int;
        updatedInteraction.position = newIndex + 1;
        return updatedInteraction;
      }
      if (index > oldIndex && index <= newIndex) {
        const updatedInteraction = int;
        updatedInteraction.position -= 1;
        return updatedInteraction;
      }
      if ((index < oldIndex && index >= newIndex) || index === newIndex) {
        const updatedInteraction = int;
        updatedInteraction.position += 1;
        return updatedInteraction;
      }
      return int;
    });
    const sortedInteractions = updatedInteractions.sort(
      (a, b) => a.position - b.position,
    );
    setInteractions(sortedInteractions);
  };

  const moveInteraction = useCallback(
    (dragIndex, hoverIndex) => {
      setInteractions(prevCards =>
        update(interactions, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, interactions[dragIndex]],
          ],
        }).map((interaction, index) => ({
          ...interaction,
          position: index + 1,
        })),
      );
    },
    [interactions],
  );

  return (
    <div className="Conversation">
      <DNDScrollZone willReceiveDraggable={shouldCollapseInteractions} />
      <div className="Conversation-header p-32">
        <div className="d-f ai-c jc-sb">
          <Text variant={TEXT_VARIANTS.SUBHEADER}>
            {t('lessons.editor.elements.conversation_title')}
          </Text>
          <QuestionOptions
            id={questionId}
            options={content.options}
            onChange={handleUpdate}
            applyChanges={applyChanges}
            questionMetadata={{}}
          />
        </div>
        <Text variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}>
          {t('questions.types.chat_conversation.hint')}
        </Text>
      </div>

      <div className="pl-32 pr-32">
        <div className="mt-32 mb-8">
          <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
            {t('practice.on_demand_practice.conversation.title')}
          </Text>
        </div>
        <TextArea
          placeholder={t(
            'practice.on_demand_practice.conversation.placeholder',
          )}
          className="Conversation-nested-text-area w-fill bg-midnight-5 p-16 llui-text-subheader-bold bdrs-small"
          onChange={onUpdateConversationTitle}
          value={conversationTitle}
          singleLine
        />
        <div className="mt-48 mb-8">
          <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
            {t(
              'practice.on_demand_practice.editor.conversations.conversation_prompt',
            )}
          </Text>
        </div>
        <TextArea
          className="Conversation-nested-text-area w-fill bg-midnight-5 p-16 llui-text-subheader bdrs-small"
          placeholder={t(
            'practice.on_demand_practice.conversation.introduction',
          )}
          value={content.subject}
          onChange={value => onChange({ subject: value })}
        />
        <div className="mt-48 mb-8">
          <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
            {t(
              'practice.on_demand_practice.editor.conversations.customer_name',
            )}
          </Text>
        </div>
        <TextArea
          className="Conversation-nested-text-area w-fill bg-midnight-5 p-16 llui-text-subheader bdrs-small"
          placeholder={t(
            'practice.on_demand_practice.conversation.customer_placeholder',
          )}
          value={customerName}
          onChange={onUpdateCustomerName}
        />
      </div>
      <div className="mt-48">
        {interactions.map((interaction, index) => (
          <Interaction
            index={index}
            key={`interaction-${interaction.id}`}
            interaction={interaction}
            updateInteraction={onUpdateInteraction(interaction.id)}
            deleteInteraction={onDeleteInteraction}
            showDelete={interactions.length > 1}
            reorderInteractions={reorderInteractions}
            reorderInteractionsInterface={reorderInteractionsInterface}
            addResponseApi={onAddResponse(interaction.id)}
            updateResponseApi={onUpdateResponse(interaction.id)}
            deleteResponseApi={onDeleteResponse(interaction.id)}
            moveInteraction={moveInteraction}
            setShouldCollapse={setShouldCollapseInteractions}
            expanded={
              index === interactions.length - 1 && !shouldCollapseInteractions
            }
            hideBorder
            showStrongErrors
          />
        ))}
        <CustomDragLayer />
      </div>
      <div>
        <button
          type="button"
          className="border-0 c-brand-blue bg-white td-u haf-c-lhci mt-24 ml-16 mb-24"
          onClick={() => {
            onCreateInteraction();
          }}
        >
          <Icon name="add" className="mr-4" />
          {t(
            'practice.on_demand_practice.editor.conversations.add_interaction',
          )}
        </button>
      </div>
    </div>
  );
}
